import apiClient from './client';

// ---------- Types ----------

export type SatSection = 'READING_AND_WRITING' | 'MATH';
export type SatDifficulty = 'EASY' | 'MEDIUM' | 'HARD';
export type SatDomain =
  | 'INFORMATION_AND_IDEAS'
  | 'CRAFT_AND_STRUCTURE'
  | 'EXPRESSION_OF_IDEAS'
  | 'STANDARD_ENGLISH_CONVENTIONS'
  | 'ALGEBRA'
  | 'ADVANCED_MATH'
  | 'PROBLEM_SOLVING_AND_DATA_ANALYSIS'
  | 'GEOMETRY_AND_TRIGONOMETRY';

export interface SatSkill {
  id: string;
  name: string;
  domain: SatDomain;
  section: SatSection;
  description: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface SatQuestionRequest {
  section: SatSection;
  domain: SatDomain;
  skillId: string;
  difficulty: SatDifficulty;
  questionText: string;
  answerA: string;
  answerB: string;
  answerC: string;
  answerD: string;
  correctAnswer: 'A' | 'B' | 'C' | 'D';
  explanation?: string | null;
  source?: string | null;
  /** Optional — defaults to false (neutral practice inventory). */
  diagnosticOnly?: boolean | null;
}

export interface SatQuestionResponse {
  id: string;
  section: SatSection;
  domain: SatDomain;
  skillId: string;
  skillName: string;
  difficulty: SatDifficulty;
  questionText: string;
  answerA: string;
  answerB: string;
  answerC: string;
  answerD: string;
  correctAnswer: string;
  explanation: string | null;
  source: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  /** Soft-retirement flag. Retired questions stay in the DB for evidence integrity. */
  active: boolean;
  /**
   * True for reserved-purpose diagnostic inventory (MD-* / MQ-* seeded in V073).
   * These rows do NOT show up in practice-set pools. Admin UI should mark them
   * visibly so editors don't treat them as regular practice questions.
   */
  diagnosticOnly: boolean;
}

export interface SatQuestionSearchRequest {
  section?: SatSection | null;
  domain?: SatDomain | null;
  skillId?: string | null;
  difficulty?: SatDifficulty | null;
  searchText?: string | null;
}

/** Map view — one row per skill with question counts. */
export interface SatTaxonomyEntry {
  skillId: string;
  section: SatSection;
  domain: SatDomain;
  name: string;
  sortOrder: number;
  total: number;
  easy: number;
  medium: number;
  hard: number;
}

/** P5-B04: Spring Page wrapper */
export interface PagedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number; // current page (0-indexed)
  first: boolean;
  last: boolean;
  empty: boolean;
}

/** P5-B05: Bulk import result */
export interface BulkImportResult {
  totalSubmitted: number;
  totalCreated: number;
  totalFailed: number;
  errors: Array<{ index: number; message: string }>;
}

// ---------- Display helpers ----------

export const SECTION_LABELS: Record<SatSection, string> = {
  READING_AND_WRITING: 'Reading & Writing',
  MATH: 'Math',
};

export const DOMAIN_LABELS: Record<SatDomain, string> = {
  INFORMATION_AND_IDEAS: 'Information and Ideas',
  CRAFT_AND_STRUCTURE: 'Craft and Structure',
  EXPRESSION_OF_IDEAS: 'Expression of Ideas',
  STANDARD_ENGLISH_CONVENTIONS: 'Standard English Conventions',
  ALGEBRA: 'Algebra',
  ADVANCED_MATH: 'Advanced Math',
  PROBLEM_SOLVING_AND_DATA_ANALYSIS: 'Problem Solving and Data Analysis',
  GEOMETRY_AND_TRIGONOMETRY: 'Geometry and Trigonometry',
};

/**
 * Compact area names for tight spots (course map, tiles, chart axes, table cells).
 * This is the ONLY place short domain labels live; a test fails if a new ad-hoc
 * abbreviation ("Adv Math", "Std English", ...) appears elsewhere. Full names: DOMAIN_LABELS.
 */
export const SHORT_DOMAIN_LABELS: Record<SatDomain, string> = {
  INFORMATION_AND_IDEAS: 'Info & Ideas',
  CRAFT_AND_STRUCTURE: 'Craft & Structure',
  EXPRESSION_OF_IDEAS: 'Expression',
  STANDARD_ENGLISH_CONVENTIONS: 'Conventions',
  ALGEBRA: 'Algebra',
  ADVANCED_MATH: 'Adv. Math',
  PROBLEM_SOLVING_AND_DATA_ANALYSIS: 'Data Analysis',
  GEOMETRY_AND_TRIGONOMETRY: 'Geo & Trig',
};

/** Full area name; falls back to the raw code for values the client does not know yet. */
export const domainLabel = (domain: string): string =>
  (DOMAIN_LABELS as Record<string, string>)[domain] ?? domain;

/** Compact area name; falls back to the full name, then the raw code. */
export const shortDomainLabel = (domain: string): string =>
  (SHORT_DOMAIN_LABELS as Record<string, string>)[domain] ?? domainLabel(domain);

/**
 * One colour per area, as CSS custom properties (defined in globals.css for both themes).
 * Reading & Writing areas sit in the plum family, Math areas in the blue family; green is never
 * a domain colour because it means "proven". Use `domainColor(domain, alpha)` for inline styles.
 */
export const DOMAIN_COLORS: Record<SatDomain, string> = {
  INFORMATION_AND_IDEAS: '--d-info',
  CRAFT_AND_STRUCTURE: '--d-craft',
  EXPRESSION_OF_IDEAS: '--d-expr',
  STANDARD_ENGLISH_CONVENTIONS: '--d-conv',
  ALGEBRA: '--d-alg',
  ADVANCED_MATH: '--d-adv',
  PROBLEM_SOLVING_AND_DATA_ANALYSIS: '--d-data',
  GEOMETRY_AND_TRIGONOMETRY: '--d-geo',
};

export const domainColor = (domain: SatDomain, alpha = 1): string =>
  `hsl(var(${DOMAIN_COLORS[domain]}) / ${alpha})`;

export const DIFFICULTY_LABELS: Record<SatDifficulty, string> = {
  EASY: 'Easy',
  MEDIUM: 'Medium',
  HARD: 'Hard',
};

export const DOMAINS_BY_SECTION: Record<SatSection, SatDomain[]> = {
  READING_AND_WRITING: [
    'INFORMATION_AND_IDEAS',
    'CRAFT_AND_STRUCTURE',
    'EXPRESSION_OF_IDEAS',
    'STANDARD_ENGLISH_CONVENTIONS',
  ],
  MATH: [
    'ALGEBRA',
    'ADVANCED_MATH',
    'PROBLEM_SOLVING_AND_DATA_ANALYSIS',
    'GEOMETRY_AND_TRIGONOMETRY',
  ],
};

// ---------- API ----------

export const satApi = {
  // Skills (any authenticated user)
  getSkills: async (): Promise<SatSkill[]> => {
    const response = await apiClient.get<SatSkill[]>('/sat/skills');
    return response.data;
  },

  getSkillsBySection: async (section: SatSection): Promise<SatSkill[]> => {
    const response = await apiClient.get<SatSkill[]>(`/sat/skills/by-section/${section}`);
    return response.data;
  },

  getSkillsByDomain: async (domain: SatDomain): Promise<SatSkill[]> => {
    const response = await apiClient.get<SatSkill[]>(`/sat/skills/by-domain/${domain}`);
    return response.data;
  },

  /** Map view payload — admin / tutor only. Returns all 56 skills with E/M/H counts. */
  getTaxonomyWithCounts: async (): Promise<SatTaxonomyEntry[]> => {
    const response = await apiClient.get<SatTaxonomyEntry[]>('/sat/skills/taxonomy-with-counts');
    return response.data;
  },

  // Questions (admin only)
  getAllQuestions: async (): Promise<SatQuestionResponse[]> => {
    const response = await apiClient.get<SatQuestionResponse[]>('/sat/questions');
    return response.data;
  },

  getQuestionById: async (id: string): Promise<SatQuestionResponse> => {
    const response = await apiClient.get<SatQuestionResponse>(`/sat/questions/${id}`);
    return response.data;
  },

  createQuestion: async (request: SatQuestionRequest): Promise<SatQuestionResponse> => {
    const response = await apiClient.post<SatQuestionResponse>('/sat/questions', request);
    return response.data;
  },

  updateQuestion: async (id: string, request: SatQuestionRequest): Promise<SatQuestionResponse> => {
    const response = await apiClient.put<SatQuestionResponse>(`/sat/questions/${id}`, request);
    return response.data;
  },

  deleteQuestion: async (id: string): Promise<void> => {
    await apiClient.delete(`/sat/questions/${id}`);
  },

  searchQuestions: async (searchRequest: SatQuestionSearchRequest): Promise<SatQuestionResponse[]> => {
    const response = await apiClient.post<SatQuestionResponse[]>('/sat/questions/search', searchRequest);
    return response.data;
  },

  /** P5-B04: Paginated search */
  searchQuestionsPaged: async (
    searchRequest: SatQuestionSearchRequest,
    page = 0,
    size = 20,
  ): Promise<PagedResponse<SatQuestionResponse>> => {
    const response = await apiClient.post<PagedResponse<SatQuestionResponse>>(
      `/sat/questions/search/paged?page=${page}&size=${size}`,
      searchRequest,
    );
    return response.data;
  },

  /** P5-B05: Bulk import questions */
  bulkImport: async (questions: SatQuestionRequest[]): Promise<BulkImportResult> => {
    const response = await apiClient.post<BulkImportResult>('/sat/questions/bulk-import', questions);
    return response.data;
  },

  /** AI-powered question generation (feature-gated) */
  generateQuestion: async (request: SatAiGenerateRequest): Promise<SatAiGenerateResponse> => {
    const response = await apiClient.post<SatAiGenerateResponse>('/sat/questions/generate', request);
    return response.data;
  },

  /** AI-powered batch question generation (feature-gated) */
  generateBatch: async (request: SatAiGenerateRequest, count: number): Promise<SatAiGenerateResponse[]> => {
    const response = await apiClient.post<SatAiGenerateResponse[]>(
      `/sat/questions/generate-batch?count=${count}`,
      request
    );
    return response.data;
  },
};

// ---------- AI Generation Types ----------

export interface SatAiGenerateRequest {
  prompt: string;
  section?: SatSection | null;
  domain?: SatDomain | null;
  skillId?: string | null;
  difficulty?: SatDifficulty | null;
  modelId?: string | null;
}

export interface SatAiGenerateResponse {
  section: SatSection;
  domain: SatDomain;
  skillName: string | null;  // Suggested skill name from AI
  difficulty: SatDifficulty;
  questionText: string;
  answerA: string;
  answerB: string;
  answerC: string;
  answerD: string;
  correctAnswer: string;
  explanation: string | null;
  modelId: string;
  inputTokens: number;
  outputTokens: number;
}

// ---------- Report Types ----------

export interface SatDomainScoreDto {
  domain: SatDomain;
  section: SatSection;
  attempted: number;
  correct: number;
  accuracyPct: number;
  level: number; // 0=No Data, 1=≤40%, 2=≤60%, 3=≤80%, 4=>80%
}

export interface SatSkillScoreDto {
  skillId: string;
  skillName: string;
  attempted: number;
  correct: number;
  accuracyPct: number;
  level: number;
}

export interface SatAccuracyTrendPointDto {
  period: string; // e.g. "2026-02-17"
  accuracyPct: number;
  questionsAttempted: number;
}

export interface SatRecentActivityDto {
  assignmentId: string;
  templateName: string;
  completedAt: string;
  scorePct: number;
  questionCount: number;
}

export interface SatStudentReportDto {
  overallAccuracyPct: number;
  totalQuestionsAttempted: number;
  totalCorrect: number;
  overallLevel: number;
  domainScores: SatDomainScoreDto[];
  trend: SatAccuracyTrendPointDto[];
  recentActivity: SatRecentActivityDto[];
}

export interface SatOverviewReportDto {
  totalSatStudents: number;
  avgOverallAccuracy: number;
  aggregateDomainScores: SatDomainScoreDto[];
}

/** Legacy 0-4 report levels, named after the mastery bands so one word means one colour everywhere. */
export const LEVEL_LABELS: Record<number, string> = {
  0: 'No data',
  1: 'Developing',
  2: 'Approaching',
  3: 'Proficient',
  4: 'Mastered',
};

// ---------- Diagnostic Types ----------

export type SatAssignmentStatus = 'ASSIGNED' | 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED';

export interface TemplateQuestionItem {
  questionId: string;
  sortOrder: number;
  questionText: string;
  questionSection: SatSection;
  domain: string;
  difficulty: string;
}

export type SatTemplateType = 'INITIAL_DIAGNOSTIC' | 'DOMAIN_DIAGNOSTIC' | 'DOMAIN_MASTERY_CHECK';

export interface SatDiagnosticTemplateResponse {
  id: string;
  name: string;
  description: string | null;
  section: SatSection | null;
  questionCount: number;
  timeLimitMin: number;
  isActive: boolean;
  createdBy: string;
  questions: TemplateQuestionItem[];
  createdAt: string;
  updatedAt: string;
  templateType: SatTemplateType;
}

export interface SatDiagnosticTemplateRequest {
  name: string;
  description?: string | null;
  section?: SatSection | null;
  timeLimitMin: number;
  questionIds: string[];
  /** Optional — backend defaults missing values to INITIAL_DIAGNOSTIC. */
  templateType?: SatTemplateType | null;
}

export interface SatDiagnosticAssignmentResponse {
  id: string;
  templateId: string;
  templateName: string;
  studentId: string;
  studentName: string;
  tutorId: string;
  tutorName: string;
  sessionId: string | null;
  status: SatAssignmentStatus;
  questionCount: number;
  timeLimitMin: number;
  assignedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  expiresAt: string;
  scorePct: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface SatDiagnosticAssignmentRequest {
  templateId: string;
  studentId: string;
  sessionId?: string | null;
  expiresAt?: string | null;
}

/** Question data for the diagnostic player — no correct answer to prevent cheating */
export interface SatDiagnosticQuestionDto {
  questionId: string;
  sortOrder: number;
  questionText: string;
  answerA: string;
  answerB: string;
  answerC: string;
  answerD: string;
  section: SatSection;
  domain: string;
  difficulty: string;
  skillName: string;
}

export interface SatSavedAnswerDto {
  questionId: string;
  selectedAnswer: 'A' | 'B' | 'C' | 'D' | null;
}

export interface SatDiagnosticAnswerRequest {
  questionId: string;
  selectedAnswer: 'A' | 'B' | 'C' | 'D' | null;
  timeSpentSec?: number;
}

export interface SatDiagnosticAnswerResponse {
  id: string;
  assignmentId: string;
  questionId: string;
  selectedAnswer: string | null;
  isCorrect: boolean;
  timeSpentSec: number | null;
  answeredAt: string;
}

/**
 * Diagnostic submit-answer acknowledgement. Deliberately answer-free: a diagnostic
 * measures without feedback, so correctness is only available after completion
 * (see SatDiagnosticResultDto). Practice submits still return SatDiagnosticAnswerResponse.
 */
export interface SatDiagnosticAnswerAck {
  questionId: string;
  saved: boolean;
}

export interface DomainScore {
  domain: string;
  attempted: number;
  correct: number;
  scorePct: number;
}

export interface QuestionResult {
  questionId: string;
  questionText: string;
  answerA: string;
  answerB: string;
  answerC: string;
  answerD: string;
  correctAnswer: string;
  selectedAnswer: string | null;
  isCorrect: boolean;
  explanation: string | null;
  domain: string;
  skillName: string;
  timeSpentSec: number | null;
}

export interface SatDiagnosticResultDto {
  assignmentId: string;
  templateId: string;
  templateName: string;
  overallScorePct: number;
  totalQuestions: number;
  totalCorrect: number;
  totalAnswered: number;
  completedAt: string;
  domainScores: DomainScore[];
  questionResults: QuestionResult[];
  /** Every domain the form covers, incl. ones never answered. Optional: older payloads omit it. */
  coveredDomains?: string[];
}

// ---------- Diagnostic API ----------

export const satDiagnosticApi = {
  // Templates (admin CRUD, tutor+admin read)
  getActiveTemplates: async (): Promise<SatDiagnosticTemplateResponse[]> => {
    const response = await apiClient.get<SatDiagnosticTemplateResponse[]>('/sat/diagnostics/templates');
    return response.data;
  },

  getTemplateById: async (id: string): Promise<SatDiagnosticTemplateResponse> => {
    const response = await apiClient.get<SatDiagnosticTemplateResponse>(`/sat/diagnostics/templates/${id}`);
    return response.data;
  },

  createTemplate: async (request: SatDiagnosticTemplateRequest): Promise<SatDiagnosticTemplateResponse> => {
    const response = await apiClient.post<SatDiagnosticTemplateResponse>('/sat/diagnostics/templates', request);
    return response.data;
  },

  updateTemplate: async (id: string, request: SatDiagnosticTemplateRequest): Promise<SatDiagnosticTemplateResponse> => {
    const response = await apiClient.put<SatDiagnosticTemplateResponse>(`/sat/diagnostics/templates/${id}`, request);
    return response.data;
  },

  deactivateTemplate: async (id: string): Promise<void> => {
    await apiClient.post(`/sat/diagnostics/templates/${id}/deactivate`);
  },

  // Assignments (tutor assign, student play)
  assignDiagnostic: async (request: SatDiagnosticAssignmentRequest): Promise<SatDiagnosticAssignmentResponse> => {
    const response = await apiClient.post<SatDiagnosticAssignmentResponse>('/sat/diagnostics/assignments', request);
    return response.data;
  },

  getMyAssignedToStudents: async (): Promise<SatDiagnosticAssignmentResponse[]> => {
    const response = await apiClient.get<SatDiagnosticAssignmentResponse[]>('/sat/diagnostics/assignments/my-assigned');
    return response.data;
  },

  getStudentAssignments: async (studentId: string): Promise<SatDiagnosticAssignmentResponse[]> => {
    const response = await apiClient.get<SatDiagnosticAssignmentResponse[]>(`/sat/diagnostics/assignments/by-student/${studentId}`);
    return response.data;
  },

  getMyAssignments: async (): Promise<SatDiagnosticAssignmentResponse[]> => {
    const response = await apiClient.get<SatDiagnosticAssignmentResponse[]>('/sat/diagnostics/assignments/mine');
    return response.data;
  },

  getAssignment: async (id: string): Promise<SatDiagnosticAssignmentResponse> => {
    const response = await apiClient.get<SatDiagnosticAssignmentResponse>(`/sat/diagnostics/assignments/${id}`);
    return response.data;
  },

  startAssignment: async (id: string): Promise<SatDiagnosticAssignmentResponse> => {
    const response = await apiClient.post<SatDiagnosticAssignmentResponse>(`/sat/diagnostics/assignments/${id}/start`);
    return response.data;
  },

  getAssignmentQuestions: async (id: string): Promise<SatDiagnosticQuestionDto[]> => {
    const response = await apiClient.get<SatDiagnosticQuestionDto[]>(`/sat/diagnostics/assignments/${id}/questions`);
    return response.data;
  },

  /** Answers already saved for an in-progress assignment (selection only, never correctness). */
  getAssignmentAnswers: async (id: string): Promise<SatSavedAnswerDto[]> => {
    const response = await apiClient.get<SatSavedAnswerDto[]>(`/sat/diagnostics/assignments/${id}/answers`);
    return response.data;
  },

  submitAnswer: async (assignmentId: string, request: SatDiagnosticAnswerRequest): Promise<SatDiagnosticAnswerAck> => {
    const response = await apiClient.post<SatDiagnosticAnswerAck>(`/sat/diagnostics/assignments/${assignmentId}/submit-answer`, request);
    return response.data;
  },

  completeAssignment: async (id: string): Promise<SatDiagnosticResultDto> => {
    const response = await apiClient.post<SatDiagnosticResultDto>(`/sat/diagnostics/assignments/${id}/complete`);
    return response.data;
  },

  getResult: async (id: string): Promise<SatDiagnosticResultDto> => {
    const response = await apiClient.get<SatDiagnosticResultDto>(`/sat/diagnostics/assignments/${id}/result`);
    return response.data;
  },
};

// ---------- Practice Types ----------

export type SatPracticeStatus = 'AVAILABLE' | 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED';

export interface SatCreatePracticeSetRequest {
  studentId: string;
  name: string;
  section?: SatSection | null;
  domain?: SatDomain | null;
  skillId?: string | null;
  difficulty?: SatDifficulty | null;
  questionCount: number;
  timeLimitMin?: number | null;
}

export interface SatPracticeSetResponse {
  id: string;
  studentId: string;
  studentName: string;
  tutorId: string;
  tutorName: string;
  name: string;
  section: SatSection | null;
  domain: SatDomain | null;
  skillId: string | null;
  skillName: string | null;
  difficulty: SatDifficulty | null;
  questionCount: number;
  timeLimitMin: number | null;
  status: SatPracticeStatus;
  startedAt: string | null;
  completedAt: string | null;
  expiresAt: string | null;
  scorePct: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface SatPracticeResultDto {
  practiceSetId: string;
  name: string;
  overallScorePct: number;
  totalQuestions: number;
  totalCorrect: number;
  totalAnswered: number;
  completedAt: string;
  domainScores: DomainScore[];
  questionResults: QuestionResult[];
  /** Every domain the form covers, incl. ones never answered. Optional: older payloads omit it. */
  coveredDomains?: string[];
}

const REINFORCE_PRACTICE_INTERNAL_NAME = 'Reinforce Practice';

export function isReinforcePracticeSet(practiceSet: Pick<SatPracticeSetResponse, 'name' | 'studentId' | 'tutorId'>) {
  return practiceSet.name === REINFORCE_PRACTICE_INTERNAL_NAME && practiceSet.studentId === practiceSet.tutorId;
}

export function getPracticeSetDisplayName(name: string) {
  if (name === REINFORCE_PRACTICE_INTERNAL_NAME) return 'Practice set';
  // Pilot sets carry an internal "Pilot C: " prefix. Students see only the topic.
  const pilot = /^Pilot [A-Z0-9]+:\s*(.+)$/.exec(name);
  if (pilot) return pilot[1].charAt(0).toUpperCase() + pilot[1].slice(1);
  return name;
}

export const PRACTICE_STATUS_LABELS: Record<SatPracticeStatus, string> = {
  AVAILABLE: 'Available',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  EXPIRED: 'Expired',
};

// ---------- Practice API ----------

export const satPracticeApi = {
  // Tutor: create, pool size, list
  createPracticeSet: async (request: SatCreatePracticeSetRequest): Promise<SatPracticeSetResponse> => {
    const response = await apiClient.post<SatPracticeSetResponse>('/sat/practice/sets', request);
    return response.data;
  },

  getPoolSize: async (request: SatCreatePracticeSetRequest): Promise<number> => {
    const response = await apiClient.post<number>('/sat/practice/pool-size', request);
    return response.data;
  },

  getMyCreatedSets: async (): Promise<SatPracticeSetResponse[]> => {
    const response = await apiClient.get<SatPracticeSetResponse[]>('/sat/practice/sets/my-created');
    return response.data;
  },

  getStudentPracticeSets: async (studentId: string): Promise<SatPracticeSetResponse[]> => {
    const response = await apiClient.get<SatPracticeSetResponse[]>(`/sat/practice/sets/by-student/${studentId}`);
    return response.data;
  },

  // Student: my sets, start, submit, complete, questions
  getMyPracticeSets: async (): Promise<SatPracticeSetResponse[]> => {
    const response = await apiClient.get<SatPracticeSetResponse[]>('/sat/practice/sets/mine');
    return response.data;
  },

  startPracticeSet: async (id: string): Promise<SatPracticeSetResponse> => {
    const response = await apiClient.post<SatPracticeSetResponse>(`/sat/practice/sets/${id}/start`);
    return response.data;
  },

  submitAnswer: async (practiceSetId: string, request: SatDiagnosticAnswerRequest): Promise<SatDiagnosticAnswerResponse> => {
    const response = await apiClient.post<SatDiagnosticAnswerResponse>(`/sat/practice/sets/${practiceSetId}/submit-answer`, request);
    return response.data;
  },

  completePracticeSet: async (id: string): Promise<SatPracticeResultDto> => {
    const response = await apiClient.post<SatPracticeResultDto>(`/sat/practice/sets/${id}/complete`);
    return response.data;
  },

  getPracticeSetQuestions: async (id: string): Promise<SatDiagnosticQuestionDto[]> => {
    const response = await apiClient.get<SatDiagnosticQuestionDto[]>(`/sat/practice/sets/${id}/questions`);
    return response.data;
  },

  /** Answers already saved for an in-progress practice set (selection only, never correctness). */
  getPracticeSetAnswers: async (id: string): Promise<SatSavedAnswerDto[]> => {
    const response = await apiClient.get<SatSavedAnswerDto[]>(`/sat/practice/sets/${id}/answers`);
    return response.data;
  },

  getResult: async (id: string): Promise<SatPracticeResultDto> => {
    const response = await apiClient.get<SatPracticeResultDto>(`/sat/practice/sets/${id}/result`);
    return response.data;
  },

  getPracticeSet: async (id: string): Promise<SatPracticeSetResponse> => {
    const response = await apiClient.get<SatPracticeSetResponse>(`/sat/practice/sets/${id}`);
    return response.data;
  },
};

// ---------- Report API ----------

export const satReportApi = {
  /** Student fetches their own report */
  getMyReport: async (): Promise<SatStudentReportDto> => {
    const response = await apiClient.get<SatStudentReportDto>('/sat/reports/my');
    return response.data;
  },

  /** Tutor/Admin fetches a specific student's report */
  getStudentReport: async (studentId: string): Promise<SatStudentReportDto> => {
    const response = await apiClient.get<SatStudentReportDto>(`/sat/reports/student/${studentId}`);
    return response.data;
  },

  /** Admin fetches aggregate overview report */
  getOverviewReport: async (): Promise<SatOverviewReportDto> => {
    const response = await apiClient.get<SatOverviewReportDto>('/sat/reports/overview');
    return response.data;
  },
};

// ---------- Layer 4 — Student SAT Profile Overview ----------
// 1:1 wire types for backend records under com.tutortalk.sat.profile.dto.
// See backend/src/main/java/com/tutortalk/sat/profile/CLAUDE.md and
// docs/sat-l4-design.md for the source-of-truth shapes.

export type SatActivityType =
  | 'INITIAL_DIAGNOSTIC'
  | 'DOMAIN_DIAGNOSTIC'
  | 'SKILL_PRACTICE'
  | 'HOMEWORK_PRACTICE'
  | 'DOMAIN_MASTERY_CHECK'
  | 'FULL_PRACTICE_TEST';

export type SatProfilePhase =
  | 'NOT_STARTED'
  | 'INITIAL_DIAGNOSTIC_ASSIGNED'
  | 'INITIAL_DIAGNOSTIC_IN_PROGRESS'
  | 'IN_DOMAIN_WORK'
  | 'BETWEEN_DOMAINS'
  | 'COMPLETED';

export type SatDomainStatus =
  | 'NOT_STARTED'
  | 'IN_PROGRESS'
  | 'READY_FOR_MASTERY_CHECK'
  | 'MASTERY_CHECK_ASSIGNED'
  | 'MASTERY_CHECK_COMPLETED'
  | 'MASTERED'
  | 'SKIPPED';

export type SatSkillStatus = 'NOT_PRACTICED' | 'PRACTICED' | 'DEFERRED';

export type SatMasteryBand =
  | 'NOT_STARTED'
  | 'INSUFFICIENT_EVIDENCE'
  | 'DEVELOPING'
  | 'APPROACHING'
  | 'PROFICIENT'
  | 'MASTERED_CANDIDATE';

export type SatConfidence = 'LOW' | 'MEDIUM' | 'HIGH';

export type SatTrendCode =
  | 'NOT_ENOUGH_RECENT_EVIDENCE'
  | 'IMPROVING'
  | 'STABLE'
  | 'DECLINING'
  | 'INCONSISTENT';

export type SatSignalCode =
  | 'INSUFFICIENT_EVIDENCE'
  | 'NOT_ENOUGH_RECENT_EVIDENCE'
  | 'STALE_EVIDENCE'
  | 'DECLINING'
  | 'INCONSISTENT'
  | 'REGRESSION_RISK'
  | 'REVIEW_RECOMMENDED'
  | 'MASTERED_BUT_AT_RISK'
  | 'CONFLICTING_EVIDENCE'
  | 'LIMITED_DOMAIN_COVERAGE'
  | 'HOMEWORK_ONLY_EVIDENCE'
  | 'PAUSED'
  | 'PREREQUISITE_GAP';

export type SatSignalSeverity = 'INFO' | 'WARNING' | 'CRITICAL';

export type SatSuggestedActionCode =
  | 'PAUSED'
  | 'INITIAL_DIAGNOSTIC_NEEDED'
  | 'STUDENT_TO_COMPLETE_INITIAL'
  | 'REVIEW_INITIAL_DIAGNOSTIC'
  | 'SELECT_ACTIVE_DOMAIN'
  | 'ASSIGN_DOMAIN_DIAGNOSTIC'
  | 'SELECT_ACTIVE_SKILL'
  | 'CONTINUE_SKILL_PRACTICE'
  | 'ASSIGN_MASTERY_CHECK'
  | 'REVIEW_MASTERY_CHECK'
  | 'REVIEW_PREVIOUSLY_MASTERED_SKILL'
  | 'COLLECT_MORE_EVIDENCE'
  | 'TUTOR_REVIEW_NEEDED'
  | 'JOURNEY_COMPLETE'
  | 'MANUAL_REVIEW_NEEDED';

export type SatTimelineEntryKind = 'ACTIVITY' | 'JOURNEY_EVENT';

export interface SatStudentBasicDto {
  id: string;
  fullName: string;
  email: string;
}

export interface SatProfileSignalDto {
  code: SatSignalCode;
  severity: SatSignalSeverity;
  title: string;
  detail: string;
  domain: SatDomain | null;
  skillId: string | null;
  skillName: string | null;
  attemptId: string | null;
  numbers: Record<string, number> | null;
}

export type SatJourneyMode = 'TUTOR_LED' | 'SELF_SERVE';

export interface SatProfileHeroDto {
  phase: SatProfilePhase;
  journeyMode: SatJourneyMode;
  paused: boolean;
  pausedReason: string | null;
  pausedAt: string | null;
  activeDomain: SatDomain | null;
  activeSkillId: string | null;
  activeSkillName: string | null;
  lastActivityAt: string | null;
  initialDiagnosticAttemptId: string | null;
  initialDiagnosticCompletedAt: string | null;
  /** correct ÷ questions on the form (skips count as not correct) — matches the score report. */
  initialDiagnosticScorePct: number | null;
  /** Questions the student actually answered ("11 answered"); null without an initial diagnostic. */
  initialDiagnosticAnsweredCount?: number | null;
  /** Questions on the form ("3 of 24 correct"); null without an initial diagnostic. */
  initialDiagnosticQuestionCount?: number | null;
  journeyStartedAt: string | null;
  totalActivitiesCompleted: number;
  totalQuestionsAnswered: number;
  profileSignals: SatProfileSignalDto[];
}

export interface SatDomainMasteryOverallDto {
  band: SatMasteryBand;
  scorePct: number | null;
  effectiveN: number | null;
  confidence: SatConfidence;
  contributingSkillsCount: number;
  totalSkillsInDomain: number;
  insufficientReason: string | null;
}

export interface SatDomainMasteryRecentDto {
  scorePct: number | null;
  accuracyPct: number | null;
  evidenceCount: number;
  windowDays: number;
}

export interface SatDomainMasterySnapshotDto {
  overall: SatDomainMasteryOverallDto;
  recent: SatDomainMasteryRecentDto;
  trend: SatTrendCode;
}

export interface SatWorkflowVsEvidenceDto {
  inSync: boolean;
  summary: string;
}

export interface SatDomainMapEntryDto {
  domain: SatDomain;
  domainLabel: string;
  section: SatSection;
  workflowStatus: SatDomainStatus;
  readyForMasteryCheck: boolean;
  masteredAt: string | null;
  lastActivityAt: string | null;
  lastMasteryCheckAttemptId: string | null;
  lastMasteryCheckScorePct: number | null;
  lastMasteryCheckAt: string | null;
  masterySnapshot: SatDomainMasterySnapshotDto;
  workflowVsEvidence: SatWorkflowVsEvidenceDto;
  signals: SatProfileSignalDto[];
}

export interface SatSkillMasteryOverallDto {
  band: SatMasteryBand;
  scorePct: number | null;
  rawAccuracyPct: number | null;
  effectiveN: number | null;
  confidence: SatConfidence;
  insufficientReason: string | null;
}

export interface SatSkillMasteryRecentDto {
  scorePct: number | null;
  accuracyPct: number | null;
  effectiveN: number | null;
  evidenceCount: number;
  windowDays: number;
}

export interface SatSkillMasterySnapshotDto {
  overall: SatSkillMasteryOverallDto;
  recent: SatSkillMasteryRecentDto;
  trend: SatTrendCode;
}

export interface SatActivityTypeBreakdownDto {
  activityType: SatActivityType;
  attempted: number;
  correct: number;
  accuracyPct: number;
  lastAttemptedAt: string | null;
}

export interface SatDifficultyBreakdownDto {
  difficulty: SatDifficulty;
  attempted: number;
  correct: number;
  accuracyPct: number;
}

export interface SatSkillEvidenceSummaryDto {
  totalEvidenceCount: number;
  recentEvidenceCount: number;
  correctCount: number;
  incorrectCount: number;
  skippedCount: number;
  unweightedOverallAccuracyPct: number | null;
  unweightedRecentAccuracyPct: number | null;
  byActivityType: SatActivityTypeBreakdownDto[];
  byDifficulty: SatDifficultyBreakdownDto[];
  firstEvidenceAt: string | null;
  lastEvidenceAt: string | null;
  daysSinceLastEvidence: number | null;
  strongestDifficulty: SatDifficulty | null;
  weakestDifficulty: SatDifficulty | null;
  recentMissedQuestionIds: string[];
  overallBand: SatMasteryBand;
  overallScorePct: number | null;
  overallEffectiveN: number | null;
  overallConfidence: SatConfidence;
  insufficientReason: string | null;
  priorMean: number;
  priorStrength: number;
}

export interface SatRelatedSkillSignalDto {
  skillId: string;
  skillName: string;
  relationship: string;
  strength: number | null;
  signalCode: SatSignalCode;
}

export interface SatProfileSkillBreakdownDto {
  skillId: string;
  skillName: string;
  skillCode: string | null;
  workflowStatus: SatSkillStatus;
  firstPracticedAt: string | null;
  lastPracticedAt: string | null;
  mastery: SatSkillMasterySnapshotDto;
  evidenceSummary: SatSkillEvidenceSummaryDto;
  signals: SatProfileSignalDto[];
  relatedWeakSkills: SatRelatedSkillSignalDto[];
}

export interface SatActiveDomainBreakdownDto {
  domain: SatDomain;
  domainMastery: SatDomainMasterySnapshotDto;
  skills: SatProfileSkillBreakdownDto[];
  signals: SatProfileSignalDto[];
}

export interface SatSuggestedActionDto {
  code: SatSuggestedActionCode;
  headline: string;
  detail: string | null;
  domain: SatDomain | null;
  skillId: string | null;
  skillName: string | null;
  attemptId: string | null;
  assignmentId: string | null;
}

export interface SatTimelineEntryDto {
  kind: SatTimelineEntryKind;
  occurredAt: string;
  title: string;
  activityType: SatActivityType | null;
  status: string | null;
  domain: SatDomain | null;
  skillId: string | null;
  attemptId: string | null;
  assignmentId: string | null;
  practiceSetId: string | null;
  scorePct: number | null;
  actorRole: string | null;
}

export interface SatStudentProfileOverviewDto {
  student: SatStudentBasicDto;
  hero: SatProfileHeroDto;
  domainMap: SatDomainMapEntryDto[];
  activeDomainBreakdown: SatActiveDomainBreakdownDto | null;
  suggestedNextAction: SatSuggestedActionDto;
  recentTimeline: SatTimelineEntryDto[];
  computedAt: string;
  recentWindowDays: number;
}

export interface SatProfileOverviewOptions {
  activeDomain?: SatDomain | null;
  recentWindowDays?: number | null;
}

export interface SatStudentProfileCardDto {
  studentId: string;
  fullName: string;
  email: string;
  phase: SatProfilePhase;
  paused: boolean;
  pausedAt: string | null;
  activeDomain: SatDomain | null;
  activeSkillId: string | null;
  activeSkillName: string | null;
  lastActivityAt: string | null;
  journeyStartedAt: string | null;
  totalActivitiesCompleted: number;
  hasOpenAssignment: boolean;
  openAssignmentAssignedAt: string | null;
}

export const satProfileApi = {
  getOverview: async (
    studentId: string,
    opts: SatProfileOverviewOptions = {},
  ): Promise<SatStudentProfileOverviewDto> => {
    const params: Record<string, string | number> = {};
    if (opts.activeDomain) params.activeDomain = opts.activeDomain;
    if (opts.recentWindowDays != null) params.recentWindowDays = opts.recentWindowDays;
    const response = await apiClient.get<SatStudentProfileOverviewDto>(
      `/sat/students/${studentId}/profile/overview`,
      { params },
    );
    return response.data;
  },

  /** Student SELF-read — same composed L4 read model, scoped to the authenticated student. */
  getMyOverview: async (
    opts: SatProfileOverviewOptions = {},
  ): Promise<SatStudentProfileOverviewDto> => {
    const params: Record<string, string | number> = {};
    if (opts.activeDomain) params.activeDomain = opts.activeDomain;
    if (opts.recentWindowDays != null) params.recentWindowDays = opts.recentWindowDays;
    const response = await apiClient.get<SatStudentProfileOverviewDto>(
      '/sat/me/profile/overview',
      { params },
    );
    return response.data;
  },

  /** Tutor/admin: switch who drives the journey — TUTOR_LED or SELF_SERVE (autopilot). */
  setJourneyMode: async (studentId: string, mode: SatJourneyMode): Promise<void> => {
    await apiClient.put(`/sat/students/${studentId}/profile/mode`, { mode });
  },

  getDirectory: async (): Promise<SatStudentProfileCardDto[]> => {
    const response = await apiClient.get<SatStudentProfileCardDto[]>(
      '/sat/students/profile-cards',
    );
    return response.data;
  },
};

/**
 * Layer-3 journey mutations — distinct from the read-only L4 overview.
 * The PUT endpoints accept `null` to clear; we mirror that here.
 */
export const satJourneyApi = {
  setActiveDomain: async (studentId: string, domain: SatDomain | null): Promise<void> => {
    await apiClient.put(`/sat/students/${studentId}/profile/active-domain`, { domain });
  },
};

export interface SatSelfEnrollResponse {
  satEnabled: boolean;
  satJourneyMode: SatJourneyMode;
}

/**
 * Self-serve SAT signup (specs/007, Option A). Idempotent — safe to call
 * even if the student is already enrolled. 403s for non-students.
 */
export const selfEnrollSat = async (): Promise<SatSelfEnrollResponse> => {
  const response = await apiClient.post<SatSelfEnrollResponse>('/sat/self-enroll');
  return response.data;
};


// ==================== Admin Course Ops board ====================

export type SatMasteryCheckStatus = 'REAL' | 'PLACEHOLDER' | 'NONE';

export interface SatCourseOpsDomain {
  domain: SatDomain;
  section: SatSection;
  practiceEasy: number;
  practiceMedium: number;
  practiceHard: number;
  practiceTotal: number;
  skillsTotal: number;
  skillsThin: number;
  miniQuizExists: boolean;
  masteryCheck: SatMasteryCheckStatus;
}

export interface SatCourseOpsDto {
  generatedAt: string;
  practiceBankTotal: number;
  reservedTotal: number;
  practiceBankTarget: number;
  domains: SatCourseOpsDomain[];
}

export const satCourseOpsApi = {
  get: async (): Promise<SatCourseOpsDto> => {
    const response = await apiClient.get<SatCourseOpsDto>('/sat/admin/course-ops');
    return response.data;
  },
};


// ==================== Study plan + score benchmarks (V090) ====================

export type SatBenchmarkSource =
  | 'OFFICIAL'
  | 'PSAT'
  | 'SELF_REPORTED'
  | 'BLUEBOOK_UPLOAD'
  | 'IN_SYSTEM';

export const BENCHMARK_SOURCE_LABELS: Record<SatBenchmarkSource, string> = {
  OFFICIAL: 'Official SAT',
  PSAT: 'PSAT',
  SELF_REPORTED: 'Self-reported practice test',
  BLUEBOOK_UPLOAD: 'Bluebook practice test',
  IN_SYSTEM: 'Mentara practice test',
};

export type SatCbModule =
  | 'RW_MODULE_1'
  | 'RW_MODULE_2_EASIER'
  | 'RW_MODULE_2_HARDER'
  | 'MATH_MODULE_1'
  | 'MATH_MODULE_2_EASIER'
  | 'MATH_MODULE_2_HARDER';

export type SatCadenceRationale =
  | 'CRUNCH_MODE'
  | 'SERIOUS_PACE'
  | 'STANDARD_PACE'
  | 'LIGHT_PACE'
  | 'BUDGET_LIMITED'
  | 'LONG_HORIZON_CAP';

export interface SatBenchmarkDto {
  id: string;
  source: SatBenchmarkSource;
  totalScore: number | null;
  rwScore: number | null;
  mathScore: number | null;
  takenAt: string; // yyyy-MM-dd
  cbTestNumber: number | null;
  partial: boolean;
  notes: string | null;
  questionResultCount: number;
  /** Skill-evidence rows created via the CB question map (creation response only). */
  evidenceCreated: number;
  createdAt: string;
}

export interface SatCadenceDto {
  recommendedIntervalDays: number;
  nextTestDueOn: string; // yyyy-MM-dd
  recommendedCbTestNumber: number | null;
  retake: boolean;
  dressRehearsal: boolean;
  testsRemaining: number;
  usedCbTestNumbers: number[];
  baselineNeeded: boolean;
  rationale: SatCadenceRationale;
}

export interface SatStudyPlanDto {
  testDate: string | null; // yyyy-MM-dd
  targetScore: number | null;
  weeklyPaceHours: number | null;
  gradeLevel: number | null;
  benchmarks: SatBenchmarkDto[];
  cadence: SatCadenceDto;
}

export interface SatStudyPlanRequest {
  testDate: string | null;
  targetScore: number | null;
  weeklyPaceHours: number | null;
  gradeLevel: number | null;
}

export interface SatQuestionResultRequest {
  module: SatCbModule;
  questionNumber: number;
  correct: boolean;
  selectedAnswer?: string | null;
}

export interface SatBenchmarkRequest {
  source: SatBenchmarkSource;
  totalScore?: number | null;
  rwScore?: number | null;
  mathScore?: number | null;
  takenAt: string; // yyyy-MM-dd
  cbTestNumber?: number | null;
  notes?: string | null;
  questionResults?: SatQuestionResultRequest[] | null;
}

export interface SatParsedUploadDto {
  cbTestNumber: number | null;
  takenAt: string | null;
  totalScore: number | null;
  rwScore: number | null;
  mathScore: number | null;
  questionResults: SatQuestionResultRequest[];
  visibleTestName: string | null;
  warnings: string[];
  inputTokens: number;
  outputTokens: number;
}

export const satPlanApi = {
  /**
   * AI extraction of My Practice screenshots into a DRAFT. Persists nothing —
   * the student reviews and confirms, then we call recordMyBenchmark.
   */
  parseBluebookUpload: async (files: File[]): Promise<SatParsedUploadDto> => {
    const form = new FormData();
    files.forEach((f) => form.append('files', f));
    const response = await apiClient.post<SatParsedUploadDto>(
      '/sat/me/benchmarks/parse-upload',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  /** Student self-read: declared plan + benchmark trajectory + cadence recommendation. */
  getMyPlan: async (): Promise<SatStudyPlanDto> => {
    const response = await apiClient.get<SatStudyPlanDto>('/sat/me/plan');
    return response.data;
  },

  /** Full-replace plan write — nulls clear (wizard is skippable, everything editable). */
  updateMyPlan: async (request: SatStudyPlanRequest): Promise<SatStudyPlanDto> => {
    const response = await apiClient.put<SatStudyPlanDto>('/sat/me/plan', request);
    return response.data;
  },

  recordMyBenchmark: async (request: SatBenchmarkRequest): Promise<SatBenchmarkDto> => {
    const response = await apiClient.post<SatBenchmarkDto>('/sat/me/benchmarks', request);
    return response.data;
  },

  /** Tutor/admin variants — RelationshipThread-gated server-side. */
  getStudentPlan: async (studentId: string): Promise<SatStudyPlanDto> => {
    const response = await apiClient.get<SatStudyPlanDto>(`/sat/students/${studentId}/plan`);
    return response.data;
  },

  updateStudentPlan: async (
    studentId: string,
    request: SatStudyPlanRequest,
  ): Promise<SatStudyPlanDto> => {
    const response = await apiClient.put<SatStudyPlanDto>(
      `/sat/students/${studentId}/plan`,
      request,
    );
    return response.data;
  },

  recordStudentBenchmark: async (
    studentId: string,
    request: SatBenchmarkRequest,
  ): Promise<SatBenchmarkDto> => {
    const response = await apiClient.post<SatBenchmarkDto>(
      `/sat/students/${studentId}/benchmarks`,
      request,
    );
    return response.data;
  },
};
